import { forwardRef, useEffect, useState } from "react";
import type { HorizontalSize } from "react-virtualized-auto-sizer";
import AutoSizer from "react-virtualized-auto-sizer";
import type { ListChildComponentProps, ReactElementType } from "react-window";
import { VariableSizeList as List } from "react-window";
import { useTheme } from "styled-components";

import type { DropdownMenuStyles } from "@/components/QuickSearch/components/styles/DropdownMenuStyle";
import DropdownMenu from "@/components/QuickSearch/components/styles/DropdownMenuStyle";
import type { PolymorphicBoardOption } from "@/components/QuickSearch/hooks/usePolyBoardOptions";
import { useScrollToSelectedOption } from "@/components/QuickSearch/hooks/useScrollToSelectedOption";
import { get } from "@/utils/theme-gets";

export type BasePolyOption = {
  height: number;
};

type RenderMenuProps = DropdownMenuStyles & {
  options: BasePolyOption[];
  selectedIndex: number;
  virtualizedItem: React.FC<ListChildComponentProps<PolymorphicBoardOption[]>>;
};

export const RenderMenu: React.FC<RenderMenuProps> = ({
  options,
  selectedIndex,
  virtualizedItem,
  menuHeight,
  ...rest
}) => {
  const { listRef, onSetListRef } = useScrollToSelectedOption(selectedIndex);
  const restObj = rest as { [key: string]: unknown };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [inner, setInner] = useState<HTMLDivElement>();
  const onSetInnerRef = (elem: HTMLDivElement) => {
    setInner(elem);
  };

  useEffect(() => {
    listRef.current?.resetAfterIndex(0);
  }, [listRef, options]);

  return (
    <DropdownMenu menuHeight={menuHeight} data-testid={restObj["data-testId"]}>
      {options?.length > 0 && (
        <AutoSizer disableHeight>
          {(size: HorizontalSize) => (
            <List
              height={menuHeight}
              width={size.width}
              itemCount={options.length}
              itemData={options}
              itemSize={(index) => options[index].height}
              innerElementType={InnerElementType as ReactElementType}
              ref={onSetListRef}
              innerRef={onSetInnerRef}
            >
              {virtualizedItem}
            </List>
          )}
        </AutoSizer>
      )}
    </DropdownMenu>
  );
};

const InnerElementType = forwardRef<HTMLDivElement, ListChildComponentProps>(({ style, ...rest }, ref) => {
  const theme = useTheme();
  return (
    <div
      ref={ref}
      style={{
        ...style,
        height: `calc(${style.height}px + ${get(theme, "spacing.s", "") as string} * 2)`,
      }}
      {...rest}
    />
  );
});
